import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API, BACKEND_URL } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Calendar, Users, FileText, CreditCard, CheckCircle, ChevronDown,
  ArrowRight, MessageCircle, BarChart3, Shield, Zap, Star,
  Upload, ArrowLeft, Loader2
} from "lucide-react";

const LandingPage = () => {
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [planesLoading, setPlanesLoading] = useState(true);

  // Subscription flow state
  const [flowStep, setFlowStep] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [regForm, setRegForm] = useState({ nombre: "", nombre_empresa: "", telefono: "", correo: "", tipo_negocio: "", password: "", confirmar: "", dias_trial: "0" });
  const [submitting, setSubmitting] = useState(false);
  const [suscripcionId, setSuscripcionId] = useState(null);
  const [trialActivoResp, setTrialActivoResp] = useState(false);
  // Iter132 · Token público firmado para subir comprobante sin sesión
  const [comprobanteToken, setComprobanteToken] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // BUG FIX: Trial NO se muestra a todo mundo. Sólo si viene ?trial=7|15|30 en la URL
  // (enlace privado que la dueña envía a personas clave) o ?trial=1 (mostrar los 3 botones).
  const trialOverride = (() => {
    const p = new URLSearchParams(window.location.search).get("trial");
    if (!p) return null;
    if (["7", "15", "30"].includes(p)) return { days: Number(p), mode: "fixed" };
    if (["1", "true", "yes"].includes(p.toLowerCase())) return { mode: "all" };
    return null;
  })();

  const datosBancarios = {
    banco: "Nu Mexico",
    titular: "Liliana Peralta Magallon",
    clabe: "638180000118294197",
    whatsapp: "9993388933"
  };

  useEffect(() => {
    axios.get(`${API}/planes/publico`).then(res => {
      setPlanes(res.data);
      setPlanesLoading(false);
    }).catch(() => setPlanesLoading(false));
  }, []);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setFlowStep("registro");
  };

  const handleRegistro = async () => {
    if (!regForm.nombre || !regForm.nombre_empresa || !regForm.telefono || !regForm.correo) {
      toast.error("Nombre, nombre de empresa, teléfono y correo son obligatorios");
      return;
    }
    if (!regForm.password || regForm.password.length < 6) {
      toast.error("La contrasena debe tener al menos 6 caracteres");
      return;
    }
    if (regForm.password !== regForm.confirmar) {
      toast.error("Las contrasenas no coinciden");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/suscripciones/registro`, {
        plan_id: selectedPlan.plan_id,
        nombre: regForm.nombre,
        nombre_empresa: regForm.nombre_empresa || regForm.nombre,
        telefono: regForm.telefono,
        correo: regForm.correo,
        tipo_negocio: regForm.tipo_negocio || null,
        password: regForm.password,
        dias_trial: Number(regForm.dias_trial) || 0,
        intencion: Number(regForm.dias_trial) > 0 ? "prueba_gratis" : "compra",
      });
      setSuscripcionId(data.suscripcion_id);
      setComprobanteToken(data.comprobante_token || null);
      // Ticket #228412 · Persistir token/id para sobrevivir recargas del navegador.
      try {
        sessionStorage.setItem("comprobante_token", data.comprobante_token || "");
        sessionStorage.setItem("suscripcion_id", data.suscripcion_id || "");
      } catch {}
      // Si eligió trial, saltar directo a confirmación (no requiere comprobante)
      setTrialActivoResp(!!data.trial_activo);
      setFlowStep(data.trial_activo ? "confirmacion" : "banco");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubirComprobante = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error("Solo se permiten archivos JPG, PNG o PDF");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Ticket #228412 · El flujo público SIEMPRE usa /comprobante-publico con token firmado.
      // Nunca caer al endpoint autenticado (provocaba "No autenticado" sin sesión).
      // El token se recupera de sessionStorage si la página fue recargada.
      let token = comprobanteToken;
      let sid = suscripcionId;
      if (!token || !sid) {
        try {
          token = token || sessionStorage.getItem("comprobante_token");
          sid = sid || sessionStorage.getItem("suscripcion_id");
        } catch {}
      }
      if (!token || !sid) {
        toast.error("Tu enlace de subida expiró (suele ocurrir al recargar la página). Inicia nuevamente el registro del plan para subir tu comprobante.");
        return;
      }
      const url = `${API}/suscripciones/${sid}/comprobante-publico?token=${encodeURIComponent(token)}`;
      // BUG FIX: NO forzar Content-Type; axios calcula el boundary del multipart automáticamente.
      await axios.post(url, formData);
      setFlowStep("confirmacion");
      toast.success("Comprobante enviado correctamente");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Error al subir comprobante";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const closeFlow = () => {
    setFlowStep(null);
    setSelectedPlan(null);
    setRegForm({ nombre: "", nombre_empresa: "", telefono: "", correo: "", tipo_negocio: "", password: "", confirmar: "" });
    setSuscripcionId(null);
  };

  const features = [
    { icon: Users, title: "CRM de Clientes", desc: "Captura, organiza y da seguimiento a todos tus clientes desde un solo lugar." },
    { icon: FileText, title: "Cotizador Automatico", desc: "Genera cotizaciones profesionales al instante con paquetes y extras configurables." },
    { icon: Calendar, title: "Gestion de Eventos", desc: "Controla fechas, servicios, staff, menu y checklist de cada evento." },
    { icon: CreditCard, title: "Control de Pagos", desc: "Registra anticipos, saldos y valida pagos. Todo bajo control financiero." },
    { icon: BarChart3, title: "Dashboard en Tiempo Real", desc: "Visualiza ventas, eventos y metricas clave al instante." },
    { icon: MessageCircle, title: "WhatsApp Integrado", desc: "Envia cotizaciones y contacta clientes directamente desde el sistema." },
  ];

  const faqs = [
    { q: "Como empiezo a usar CelebraMid?", a: "Selecciona tu plan y regístrate. Recibes 15 días de prueba gratuita al instante, sin comprobante de pago. Al vencer, subes tu comprobante y activamos tu acceso." },
    { q: "Cuánto dura la prueba gratuita?", a: "15 días naturales desde tu registro. Se aplica una vez por empresa y conserva todos tus datos al vencer, aunque no podrás crear nuevos registros hasta contratar." },
    { q: "Puedo cambiar de plan despues?", a: "Sí, puedes subir o bajar tu plan en cualquier momento. Los cambios se aplican en tu próximo ciclo de facturación." },
    { q: "Mis datos estan seguros?", a: "Usamos encriptación de extremo a extremo y servidores seguros. Tus datos son solo tuyos." },
    { q: "Necesito experiencia tecnica?", a: "No. CelebraMid está diseñado para ser intuitivo. Si sabes usar WhatsApp, sabes usar CelebraMid." },
    { q: "Puedo agregar mas usuarios?", a: "Organiza incluye 1 usuario, Opera 3 y Negocio 6. Cada usuario adicional cuesta $99 MXN + IVA al mes en cualquier plan." },
    { q: "Los precios incluyen IVA?", a: "No. Los precios son mensuales en pesos mexicanos más IVA. Organiza $299, Opera $599, Negocio $899." },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-xl tracking-tight text-rose-900">
            Celebra<span className="font-bold">Mid</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="#planes" className="text-sm text-stone-600 hover:text-stone-900 hidden md:block">Planes</a>
            <a href="#funciones" className="text-sm text-stone-600 hover:text-stone-900 hidden md:block">Funciones</a>
            {user ? (
              <Link to="/panel">
                <Button className="btn-primary text-sm" data-testid="btn-panel-admin">Panel Admin</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="border-stone-300 text-sm" data-testid="btn-iniciar-sesion">Iniciar sesion</Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-950 via-rose-900 to-stone-900" />
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-rose-800/50 border border-rose-700/50 rounded-full px-4 py-1.5 mb-6">
              <Zap className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs text-rose-100 font-medium">Sistema todo-en-uno para eventos</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display text-white leading-tight">
              Organiza, vende y ejecuta tus eventos{" "}
              <span className="text-amber-300 italic">sin estres</span>
            </h1>
            <p className="text-lg text-rose-100/80 mt-6 max-w-lg">
              Controla clientes, cotizaciones, pagos y eventos desde un solo lugar. Todo lo que tu negocio de eventos necesita.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a href="#planes">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-stone-900 font-semibold w-full sm:w-auto" data-testid="btn-hero-planes">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Ver Planes
                </Button>
              </a>
              <Link to="/cotizar/user_d7abce64f0cf">
                <Button size="lg" variant="outline" className="border-rose-400/40 text-white hover:bg-rose-800/50 w-full sm:w-auto" data-testid="btn-hero-cotizar">
                  Cotizar Evento <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="funciones" className="max-w-6xl mx-auto px-4 py-16 md:py-24">
        <div className="text-center mb-12">
          <h2 className="text-base md:text-lg font-display text-rose-900">Todo lo que necesitas</h2>
          <p className="text-stone-500 mt-2 text-sm">Un sistema completo para gestionar tu negocio de eventos</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (
            <Card key={f.title} className="border-stone-200 hover:shadow-md transition-shadow group">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-rose-900 transition-colors">
                  <f.icon className="w-5 h-5 text-rose-800 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-medium text-stone-900 mb-2">{f.title}</h3>
                <p className="text-sm text-stone-500">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing - Plans from DB */}
      <section id="planes" className="bg-rose-950 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-display text-white">Planes y Precios</h2>
            <p className="text-rose-200/60 mt-2 text-sm">Elige el plan que mejor se adapte a tu negocio</p>
          </div>
          {planesLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : planes.length === 0 ? (
            <p className="text-center text-rose-300/60 py-8" data-testid="planes-no-disponibles">Planes no disponibles en este momento</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {planes.map((plan) => {
                const isAccent = plan.orden === 2;
                return (
                  <Card key={plan.plan_id} className={`border-0 ${isAccent ? 'bg-white ring-2 ring-amber-400 scale-105' : 'bg-rose-900/50 border border-rose-800'}`} data-testid={`plan-card-${plan.plan_id}`}>
                    <CardContent className="p-6 flex flex-col h-full">
                      {isAccent && (
                        <div className="flex items-center gap-1 text-amber-600 text-xs font-medium mb-3" data-testid={`badge-${plan.plan_id}`}>
                          <Star className="w-3 h-3 fill-amber-500" /> {plan.etiqueta || "Más elegido"}
                        </div>
                      )}
                      <h3 className={`font-display text-lg ${isAccent ? 'text-stone-900' : 'text-white'}`}>{plan.nombre}</h3>
                      <div className="my-4">
                        <span className={`text-3xl font-display ${isAccent ? 'text-stone-900' : 'text-white'}`}>${plan.precio}</span>
                        <span className={`text-sm ${isAccent ? 'text-stone-500' : 'text-rose-300/60'}`}>
                          {" "}{plan.moneda || "MXN"} + {plan.impuesto || "IVA"} / {plan.periodo || "mensual"}
                        </span>
                      </div>
                      <p className={`text-xs mb-3 ${isAccent ? 'text-stone-500' : 'text-rose-300/60'}`} data-testid={`plan-trial-${plan.plan_id}`}>
                        15 días de prueba gratis · Usuario adicional ${plan.precio_usuario_adicional || 99} {plan.moneda || "MXN"} + {plan.impuesto || "IVA"}/mes
                      </p>
                      <ul className="space-y-2 flex-1 mb-6">
                        {(plan.features || []).map((f) => (
                          <li key={f} className={`flex items-start gap-2 text-sm ${isAccent ? 'text-stone-600' : 'text-rose-100/80'}`}>
                            <CheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${isAccent ? 'text-rose-600' : 'text-rose-400'}`} />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <Button
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full ${isAccent ? 'bg-rose-900 hover:bg-rose-800 text-white' : 'bg-rose-800 hover:bg-rose-700 text-white'}`}
                        data-testid={`btn-plan-${plan.plan_id}`}
                      >
                        Elegir {plan.nombre}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Social Proof */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { num: "500+", label: "Eventos gestionados" },
            { num: "150+", label: "Negocios activos" },
            { num: "98%", label: "Satisfaccion" },
            { num: "24/7", label: "Soporte" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-2xl md:text-3xl font-display text-rose-900">{s.num}</p>
              <p className="text-xs text-stone-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 py-16">
        <h2 className="text-base md:text-lg font-display text-stone-900 text-center mb-8">Preguntas Frecuentes</h2>
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div key={faq.q} className="border border-stone-200 rounded-sm overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === faq.q ? null : faq.q)}
                className="w-full p-4 text-left flex items-center justify-between text-sm font-medium text-stone-900 hover:bg-stone-50"
                data-testid={`faq-${faq.q.slice(0, 20).replace(/\s/g, '-')}`}
              >
                {faq.q}
                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${openFaq === faq.q ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === faq.q && (
                <div className="px-4 pb-4 text-sm text-stone-600">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-rose-900 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-base md:text-lg font-display text-white mb-3">
            Lleva tu negocio de eventos al siguiente nivel
          </h2>
          <p className="text-rose-200/70 text-sm mb-6">
            Empieza hoy mismo. Sin compromisos.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#planes">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-stone-900 font-semibold">
                <CreditCard className="w-5 h-5 mr-2" /> Ver Planes y Registrarte
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 py-8 text-center">
        <p className="text-stone-500 text-xs">CelebraMid - Sistema integral de eventos y catering</p>
      </footer>

      {/* ==================== SUBSCRIPTION FLOW DIALOG ==================== */}
      <Dialog open={flowStep !== null} onOpenChange={(open) => { if (!open) closeFlow(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          {/* Step: Registro */}
          {flowStep === "registro" && selectedPlan && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Registrate en CelebraMid</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <div className="bg-rose-50 border border-rose-200 rounded-sm p-3 flex justify-between items-center">
                  <div>
                    <p className="font-medium text-rose-900">{selectedPlan.nombre}</p>
                    <p className="text-sm text-rose-700">${selectedPlan.precio} / {selectedPlan.periodo}</p>
                  </div>
                  <button onClick={closeFlow} className="text-xs text-stone-500 underline">Cambiar</button>
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Nombre completo *</Label>
                    <Input value={regForm.nombre} onChange={(e) => setRegForm(p => ({ ...p, nombre: e.target.value }))}
                      placeholder="Tu nombre" className="border-stone-300" data-testid="input-reg-nombre" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Nombre de la empresa *</Label>
                    <Input value={regForm.nombre_empresa} onChange={(e) => setRegForm(p => ({ ...p, nombre_empresa: e.target.value }))}
                      placeholder="Ej: Eventos Aurora" className="border-stone-300" data-testid="input-reg-nombre-empresa" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Telefono / WhatsApp *</Label>
                    <Input value={regForm.telefono} onChange={(e) => setRegForm(p => ({ ...p, telefono: e.target.value }))}
                      placeholder="999 123 4567" className="border-stone-300" data-testid="input-reg-telefono" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Correo electronico *</Label>
                    <Input type="email" value={regForm.correo} onChange={(e) => setRegForm(p => ({ ...p, correo: e.target.value }))}
                      placeholder="tu@email.com" className="border-stone-300" data-testid="input-reg-correo" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tipo de negocio</Label>
                    <Input value={regForm.tipo_negocio} onChange={(e) => setRegForm(p => ({ ...p, tipo_negocio: e.target.value }))}
                      placeholder="Ej: Salon de fiestas, Catering, Coordinacion" className="border-stone-300" data-testid="input-reg-tipo-negocio" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Contrasena *</Label>
                    <Input type="password" value={regForm.password} onChange={(e) => setRegForm(p => ({ ...p, password: e.target.value }))}
                      placeholder="Minimo 6 caracteres" className="border-stone-300" data-testid="input-reg-password" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Confirmar contrasena *</Label>
                    <Input type="password" value={regForm.confirmar} onChange={(e) => setRegForm(p => ({ ...p, confirmar: e.target.value }))}
                      placeholder="Repite tu contrasena" className="border-stone-300" data-testid="input-reg-confirmar" />
                  </div>
                  {regForm.password && regForm.confirmar && regForm.password !== regForm.confirmar && (
                    <p className="text-xs text-red-500">Las contrasenas no coinciden</p>
                  )}
                </div>

                {/* Selector de trial gratuito — solo visible con URL ?trial=... (enlace privado a personas clave) */}
                {trialOverride && (
                  <div className="border-t border-stone-200 pt-4">
                    <Label className="text-sm font-medium">¿Quieres probar gratis primero?</Label>
                    <p className="text-xs text-stone-500 mb-2">Enlace exclusivo · Sin tarjeta. Al finalizar tu prueba deberás subir el comprobante para continuar.</p>
                    <div className={`grid ${trialOverride.mode === "fixed" ? "grid-cols-2" : "grid-cols-4"} gap-2`}>
                      {(trialOverride.mode === "fixed"
                        ? [{ v: "0", l: "Pagar ahora" }, { v: String(trialOverride.days), l: `Prueba ${trialOverride.days} días` }]
                        : [{ v: "0", l: "Pagar ahora" }, { v: "7", l: "Prueba 7 días" }, { v: "15", l: "Prueba 15 días" }, { v: "30", l: "Prueba 30 días" }]
                      ).map((op) => (
                        <button
                          key={op.v}
                          type="button"
                          onClick={() => setRegForm(p => ({ ...p, dias_trial: op.v }))}
                          className={`px-2 py-2 text-xs rounded-md border transition ${
                            regForm.dias_trial === op.v ? "border-emerald-600 bg-emerald-50 text-emerald-800 font-medium" : "border-stone-300 hover:border-stone-500"
                          }`}
                          data-testid={`btn-trial-${op.v}`}
                        >
                          {op.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <Button onClick={handleRegistro} disabled={submitting} className="w-full btn-primary" data-testid="btn-registrar-suscripcion">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  {regForm.dias_trial !== "0" ? `Iniciar prueba de ${regForm.dias_trial} días` : "Continuar al pago"}
                </Button>
              </div>
            </>
          )}

          {/* Step: Datos Bancarios */}
          {flowStep === "banco" && selectedPlan && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Datos para transferencia</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <div className="bg-rose-50 border border-rose-200 rounded-sm p-3">
                  <p className="text-sm text-rose-800">
                    <strong>{selectedPlan.nombre}</strong> - <strong>${selectedPlan.precio} MXN / {selectedPlan.periodo}</strong>
                  </p>
                </div>

                <div className="bg-stone-50 border border-stone-200 rounded-sm p-4 space-y-3">
                  <h3 className="font-medium text-stone-900 text-sm">Realiza tu transferencia a:</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Banco</span>
                      <span className="font-medium text-stone-900">{datosBancarios.banco}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Titular</span>
                      <span className="font-medium text-stone-900">{datosBancarios.titular}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">CLABE</span>
                      <span className="font-mono font-medium text-stone-900 text-xs">{datosBancarios.clabe}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Monto</span>
                      <span className="font-medium text-rose-800">${selectedPlan.precio} MXN</span>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                  <p className="text-xs text-amber-800">
                    Una vez realizada la transferencia, sube tu comprobante de pago en el siguiente paso. Tu cuenta sera activada en minutos.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setFlowStep("registro")} className="border-stone-300 flex-1">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Atras
                  </Button>
                  <Button onClick={() => setFlowStep("comprobante")} className="btn-primary flex-1" data-testid="btn-ir-comprobante">
                    Ya pague <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>

                <p className="text-xs text-center text-stone-400">
                  Dudas? Contactanos por WhatsApp: {datosBancarios.whatsapp}
                </p>
              </div>
            </>
          )}

          {/* Step: Subir comprobante */}
          {flowStep === "comprobante" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Subir comprobante</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <p className="text-sm text-stone-600">Sube una foto o captura de tu comprobante de transferencia (JPG, PNG o PDF).</p>

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-stone-300 rounded-sm p-8 text-center cursor-pointer hover:border-rose-500 hover:bg-rose-50/30 transition-colors"
                  data-testid="drop-comprobante"
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
                      <p className="text-sm text-stone-600">Subiendo comprobante...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-stone-400" />
                      <p className="text-sm text-stone-600 font-medium">Haz clic para seleccionar archivo</p>
                      <p className="text-xs text-stone-400">JPG, PNG o PDF</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={handleSubirComprobante}
                  className="hidden"
                  data-testid="input-comprobante-file"
                />

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setFlowStep("banco")} className="border-stone-300 flex-1">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Atras
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Step: Confirmacion */}
          {flowStep === "confirmacion" && (
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-rose-600" />
              </div>
              <div>
                <h2 className="font-display text-xl text-stone-900">Registro completado</h2>
                <p className="text-sm text-stone-600 mt-2">
                  Tu cuenta ha sido creada. Ya puedes iniciar sesion para ver el estado de tu suscripcion.
                </p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-sm text-stone-600 space-y-1">
                <p><strong>Plan:</strong> {selectedPlan?.nombre} - ${selectedPlan?.precio} / {selectedPlan?.periodo}</p>
                <p><strong>Nombre:</strong> {regForm.nombre}</p>
                <p><strong>Correo:</strong> {regForm.correo}</p>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-sm p-3">
                {trialActivoResp ? (
                  <>
                    <p className="text-sm text-rose-800 font-medium">Tu período de prueba de 15 días ya está activo.</p>
                    <p className="text-xs text-rose-700 mt-1">Inicia sesión para empezar. Antes de que termine la prueba te avisaremos para que subas tu comprobante.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-rose-800 font-medium">Revisaremos tu comprobante y activaremos tu plan lo antes posible.</p>
                    <p className="text-xs text-rose-700 mt-1">Mientras tanto, puedes entrar a tu panel para ver el estado de tu solicitud.</p>
                  </>
                )}
              </div>
              <Button onClick={() => { closeFlow(); window.location.href = "/login"; }} className="w-full btn-primary" data-testid="btn-ir-login-confirmacion">
                <ArrowRight className="w-4 h-4 mr-2" /> Iniciar sesion
              </Button>
              <p className="text-xs text-stone-400">
                Dudas? Contactanos al WhatsApp: {datosBancarios.whatsapp}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
